import type { EditorView } from "@codemirror/view";
import type { Diagnostic } from "@codemirror/lint";

export interface SqlLintConfig {
  tableNames?: string[];
}

// Patterns for dangerous operations
const DANGEROUS_PATTERNS: { pattern: RegExp; message: string }[] = [
  {
    pattern: /\bDELETE\s+FROM\s+\S+\s*(?:;|\s*$)/i,
    message: "DELETE without WHERE clause — this will delete all rows",
  },
  {
    pattern: /\bTRUNCATE\b/i,
    message: "TRUNCATE will remove all rows from the table",
  },
  {
    pattern: /\bDROP\s+TABLE\b/i,
    message: "DROP TABLE will permanently remove the table and all its data",
  },
  {
    pattern: /\bDROP\s+SCHEMA\b/i,
    message: "DROP SCHEMA will permanently remove the schema and all its objects",
  },
  {
    pattern: /\bDROP\s+DATABASE\b/i,
    message: "DROP DATABASE will permanently destroy the entire database",
  },
];

// Template variable pattern
const TEMPLATE_VAR_RE = /\{\{([^}]+)\}\}/g;

/**
 * SQL linter factory. Returns a CodeMirror linter function.
 */
export function sqlLinter(config: SqlLintConfig): (view: EditorView) => Diagnostic[] {
  return (view: EditorView): Diagnostic[] => {
    const diagnostics: Diagnostic[] = [];
    const text = view.state.doc.toString();

    // Check for dangerous operations (warning)
    for (const { pattern, message } of DANGEROUS_PATTERNS) {
      // Check if DELETE without WHERE actually has no WHERE
      if (pattern.source.includes("DELETE")) {
        // More precise: find DELETE FROM ... and check if WHERE follows
        const deleteMatch = text.match(/\bDELETE\s+FROM\s+\S+/i);
        if (deleteMatch) {
          const afterDelete = text.slice(deleteMatch.index! + deleteMatch[0].length);
          if (!/\bWHERE\b/i.test(afterDelete.split(/;/)[0]!)) {
            diagnostics.push({
              from: deleteMatch.index!,
              to: deleteMatch.index! + deleteMatch[0].length,
              severity: "warning",
              message,
            });
          }
        }
        continue;
      }

      const match = text.match(pattern);
      if (match?.index !== undefined) {
        diagnostics.push({
          from: match.index,
          to: match.index + match[0].length,
          severity: "warning",
          message,
        });
      }
    }

    // Check for unresolved template variables (error)
    for (const match of text.matchAll(TEMPLATE_VAR_RE)) {
      if (match.index !== undefined) {
        const content = match[1]!;
        const isVault = /^(secret|config):/.test(content);
        if (!isVault) {
          diagnostics.push({
            from: match.index,
            to: match.index + match[0].length,
            severity: "info",
            message: `Template variable '{{${content}}}' — will be resolved at execution time`,
          });
        }
      }
    }

    // Check for tables not in schema (if catalog loaded)
    if (config.tableNames && config.tableNames.length > 0) {
      const tableNameSet = new Set(config.tableNames.map((n) => n.toLowerCase()));

      // Match FROM/JOIN/INTO/UPDATE table references
      const tableRefRe = /\b(?:FROM|JOIN|INTO|UPDATE)\s+(?:"([^"]+)"|([a-zA-Z_][a-zA-Z0-9_.]*))(?:\s|,|$)/gi;
      for (const match of text.matchAll(tableRefRe)) {
        const tableName = match[1] ?? match[2]!;
        const lowerName = tableName.toLowerCase();
        // Skip if it's a subquery alias or CTE
        if (lowerName === "select" || lowerName === "lateral") continue;
        if (!tableNameSet.has(lowerName)) {
          const nameStart = match.index! + match[0].indexOf(tableName);
          diagnostics.push({
            from: nameStart,
            to: nameStart + tableName.length,
            severity: "info",
            message: `Table '${tableName}' not found in catalog`,
          });
        }
      }
    }

    return diagnostics;
  };
}
